import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Toggle } from '@/components/ui/Toggle';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import type { DeviceStatus, RelayState } from '@/lib/types';
import { cn } from '@/lib/cn';

interface Props {
  device: DeviceStatus;
  index: number;
  relay: RelayState;
  disabled: boolean;
}

export function RelayConfigPopover({ device, index, relay, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [act, setAct]   = useState<1 | 2>((relay.actTimeSelect ?? 1) as 1 | 2);
  const [aon, setAon]   = useState<boolean>(!!relay.alwaysOn);

  const t1 = device.actTime1 ?? 10;
  const t2 = device.actTime2 ?? 20;

  const mut = useDeviceMutation<{ id: number; actTimeSelect: 1 | 2; alwaysOn: boolean }>(
    device.client_id, 'relay-config',
    {
      successToast: `Relay ${index + 1} config saved`,
      optimistic: (prev, vars) => {
        if (!prev?.relays) return prev;
        return {
          ...prev,
          relays: prev.relays.map((r, i) =>
            i === vars.id ? { ...r, actTimeSelect: vars.actTimeSelect, alwaysOn: vars.alwaysOn } : r,
          ),
        };
      },
    },
  );

  // Reset draft state when popover re-opens, so it reflects the latest server state.
  const onOpenChange = (next: boolean) => {
    if (next) {
      setAct((relay.actTimeSelect ?? 1) as 1 | 2);
      setAon(!!relay.alwaysOn);
    }
    setOpen(next);
  };

  const save = () => {
    mut.mutate({ id: index, actTimeSelect: act, alwaysOn: aon });
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button" aria-label={`Configure relay ${index + 1}`} disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'absolute top-1.5 right-1.5 p-1 rounded-md text-slate-400',
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
            'hover:bg-slate-100 hover:text-ink transition-colors',
            'disabled:cursor-not-allowed',
          )}
        >
          <Settings2 className="w-3.5 h-3.5" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end" sideOffset={6}
          className="z-50 w-64 bg-white rounded-xl border border-slate-200 shadow-lg p-4 space-y-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-sm font-bold text-ink">Relay {index + 1} · {relay.name || `R${index + 1}`}</div>

          <div>
            <span className="block text-xs font-semibold text-slate-700 mb-1.5">Actuation time</span>
            <div className="grid grid-cols-2 gap-1 bg-slate-100 p-1 rounded-lg">
              {[1, 2].map((v) => {
                const active = act === v;
                return (
                  <button
                    key={v} type="button"
                    onClick={() => setAct(v as 1 | 2)}
                    className={cn(
                      'py-1.5 rounded-md text-xs font-bold transition-colors',
                      active ? 'bg-white shadow text-ink' : 'text-slate-500 hover:text-ink',
                    )}
                  >
                    T{v} <span className="font-normal text-slate-400">({v === 1 ? t1 : t2}s)</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-xs font-semibold text-slate-700">Always on</span>
            <Toggle checked={aon} onCheckedChange={setAon} aria-label="Always on" />
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" className="flex-1" onClick={save} loading={mut.isPending}>
              Save
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
